import store from "@/store"
import { getProjectPermission } from "@/store/actions"
import { permissionMap } from "@/utils/project"

/**
 * 校验用户是否指定视图的某一权限
 * @param {String} appId 默认是当前应用的appId
 * @param {String} sheetId
 * @param {String} pri 包括C、U、R、D、E、P、TL、TM
 * @param {Object} sheetData 工作表数据
 */
export function checkPermission({ appId, sheetId, pri, sheetData } = {}) {
  if (isAppAdmin(appId)) return true
  if (!sheetId) return false
  return hasPermission(sheetId, pri, sheetData)
}

export function isSuperAdmin() {
  return Boolean(store.getters.userPermission.authorities?.includes("SUPER_ADMIN"))
}

export function isCompanyAdmin(companyId = store.getters.userInfo?.companyId) {
  if (isSuperAdmin()) return true
  // 根据companyId判断是否是该company的管理员
  return Boolean(store.getters.userPermission.authorities?.includes(`${companyId}_COMPANY_管理员`))
}

export function isAppAdmin(appId) {
  // 是否是公司管理员
  if (isCompanyAdmin()) return true
  // 根据appId判断是否是该app的管理员
  if (!appId) return false
  return Boolean(store.getters.userPermission.authorities?.includes(`${appId}_ROLE_管理员`))
}

export function hasPermission(sheetId, pri, sheetData) {
  let need = `${sheetId}_${pri}_`
  let authorities = store.getters.userPermission.authorities
  if (!authorities) return false
  let find = authorities.filter(i => i.startsWith(need))
  if (find.length == 0) return false
  let target = find[0].replace(need, "")
  if (["R", "C", "E"].includes(pri)) return target != "NONE"
  if (!sheetData || !sheetData.ownerId || !sheetData.participants) return false
  let role = getUserRole(store.getters.userInfo.userId || "", sheetData)
  return checkRangePri(role, target)
}

function getUserRole(userId, sheetData) {
  if (!userId) return "NONE"
  if (sheetData.ownerId == userId) return "CHARGE"
  if (sheetData.participants.includes(userId)) return "PART"
  else return "NONE"
}

// role代表用户的权限，target代表需要匹配的权限
function checkRangePri(role, target) {
  switch (target) {
    case "NONE":
      return false
    case "ALL":
      return true
    case "CHARGE":
      return role == "CHARGE"
    case "PART":
      return ["CHARGE", "PART"].includes(role)
  }
}

export function checkCompanyPermission({ companyId, pri } = {}) {
  if (isCompanyAdmin(companyId)) return true
  let authorities = store.getters.userPermission?.authorities
  if (!authorities) return false
  return authorities.includes(`${companyId}_COMPANYOPS_${pri}`)
}

// 如果有企业管理的菜单管理任一个权限,都可以进入企业管理后台
export function canManageCompany(companyId) {
  if (isCompanyAdmin(companyId)) return true
  let authorities = store.getters.userPermission?.authorities
  if (!authorities) return false
  let prefix = `${companyId}_COMPANYOPS_`
  return authorities.some(t => t.startsWith(prefix))
}

export function getProjectPri(projectId, companyId = store.getters.userInfo?.companyId) {
  let allPri = Object.fromEntries(Object.keys(permissionMap).map(priName => [priName, true]))
  if (isCompanyAdmin(companyId)) {
    return Promise.resolve({
      isAdmin: true,
      rolePris: {
        ADMIN: allPri
      }
    })
  }
  return getProjectPermission().then(res => {
    if (!res[projectId]) return Promise.reject("权限不足")
    if (res[projectId].rolePris) {
      res[projectId].rolePris.ADMIN = allPri
    }
    return res[projectId]
  })
}

export function getTaskUserPermission(projectPermission, taskInfo, userId) {
  if (!taskInfo?.id) return {}

  let userRoles = []
  if (projectPermission?.isAdmin) userRoles.push("ADMIN")
  if (taskInfo.assignee === userId) userRoles.push("ASSIGNEE")
  if (taskInfo.creatorId === userId) userRoles.push("CREATOR")
  if (taskInfo.participants?.includes(userId)) userRoles.push("PARTICIPANT")

  if (userRoles.includes("ADMIN")) {
    return projectPermission.rolePris["ADMIN"]
  }
  let pri = {}
  let rolePris = projectPermission.rolePris
  for (const role of userRoles) {
    for (const priKey in rolePris[role]) {
      pri[priKey] = pri[priKey] || rolePris[role][priKey]
    }
  }
  return pri
}
